<script setup lang="ts">
import FooterBar from '@/components/footer-bar/FooterBar.vue'
import { useUserStore } from '@/stores/modules/user'
import type { EquipItemType, EquipQuality, EquipType } from '@/types/equip'
import { computed, ref } from 'vue'
import { socketEmitAsync } from '@/utils/web-socket'
import type { ItemType } from '@/types/item'
import type { warehouseApiRes } from '@/types/warehouse'
import { getEquipName } from '@/utils/equip'
import { sortItemList } from '@/utils/item'
import { Lock } from '@element-plus/icons-vue'
import EquipInfo from '../item/components/EquipInfo.vue'
import { equipIcon, equipQualityList, equipQualityText, equipTypeList } from '../item/data'
import { equipAffixCollection } from '@/core/equip/affix'

const userStore = useUserStore()

const filterType = ref<EquipType[]>([])
const filterQuality = ref<EquipQuality[]>([])
const filterName = ref('')
const filterAffix = ref('')
function handleAddFilterType(type: EquipType) {
  const index = filterType.value.findIndex((item) => item === type)
  if (index !== -1) return filterType.value.splice(index, 1)
  filterType.value.push(type)
}
function handleAddFilterQuality(quality: EquipQuality) {
  const index = filterQuality.value.findIndex((item) => item === quality)
  if (index !== -1) return filterQuality.value.splice(index, 1)
  filterQuality.value.push(quality)
}
function handleResetFilter() {
  filterType.value = []
  filterQuality.value = []
  filterName.value = ''
  filterAffix.value = ''
}

const itemList = computed(() => {
  return userStore.getItemEquipInfoList.filter((item) => {
    const specialNameList = item.attrs.special?.map((item) => item.name)
    const suitAffixNameList = item.attrs.suit?.attrs.affix?.map((item) => item.name)
    const darkGoldAffixNameList = item.darkGoldAttrs?.special.map((item) => equipAffixCollection[item.key].name)
    const engraveAffixNameList = item.engraveAttr?.special.map((item) => equipAffixCollection[item.key].name)
    return (
      (filterType.value.length ? filterType.value.includes(item.type) : true) &&
      (filterQuality.value.length ? filterQuality.value.includes(item.quality) : true) &&
      item.name.includes(filterName.value) &&
      (filterAffix.value
        ? suitAffixNameList?.includes(filterAffix.value) ||
          specialNameList?.includes(filterAffix.value) ||
          darkGoldAffixNameList?.includes(filterAffix.value) ||
          engraveAffixNameList?.includes(filterAffix.value)
        : true)
    )
  })
})
const warehouseItemList = computed(() => {
  return userStore.getWarehouseItemEquipInfoList.filter((item) => {
    const specialNameList = item.attrs.special?.map((item) => item.name)
    const suitAffixNameList = item.attrs.suit?.attrs.affix?.map((item) => item.name)
    const darkGoldAffixNameList = item.darkGoldAttrs?.special.map((item) => equipAffixCollection[item.key].name)
    const engraveAffixNameList = item.engraveAttr?.special.map((item) => equipAffixCollection[item.key].name)
    return (
      (filterType.value.length ? filterType.value.includes(item.type) : true) &&
      (filterQuality.value.length ? filterQuality.value.includes(item.quality) : true) &&
      item.name.includes(filterName.value) &&
      (filterAffix.value
        ? suitAffixNameList?.includes(filterAffix.value) ||
          specialNameList?.includes(filterAffix.value) ||
          darkGoldAffixNameList?.includes(filterAffix.value) ||
          engraveAffixNameList?.includes(filterAffix.value)
        : true)
    )
  })
})

const equipInfo = ref<EquipItemType>()
async function handleArrange() {
  await socketEmitAsync<ItemType[]>({ type: 'arrangeEquip' })
  const res = sortItemList(userStore.itemList)
  userStore.itemList = res
}
async function handleArrangeWarehouse() {
  if (!userStore.warehouseItemList?.length) return
  await socketEmitAsync<ItemType[]>({ type: 'arrangeWarehouse' })
  const res = sortItemList(userStore.warehouseItemList)
  userStore.warehouseItemList = res
}

const isShowEquipInfo = ref(false)
async function warehouseAction(equipItem: EquipItemType, type: 'warehouseDeposit' | 'warehouseTakeOut') {
  if (isShowEquipInfo.value) return (equipInfo.value = equipItem)
  const { id } = equipItem
  if (type === 'warehouseDeposit') {
    if ((userStore.warehouseItemList || []).length >= userStore.getWarehouseCapacity) return ElMessage.warning('仓库已满')
  } else {
    if (userStore.getItemEquipTotal >= userStore.getItemCapacity) return ElMessage.warning('背包已满')
  }
  await socketEmitAsync<warehouseApiRes>({ type, data: { id } })

  if (!userStore.warehouseItemList) userStore.warehouseItemList = []
  if (type === 'warehouseDeposit') {
    const itemIndex = userStore.itemList.findIndex((item) => item.id === id)
    if (itemIndex === -1) return
    userStore.warehouseItemList.unshift(userStore.itemList[itemIndex])
    userStore.itemList.splice(itemIndex, 1)
  } else {
    const itemIndex = userStore.warehouseItemList.findIndex((item) => item.id === id)
    if (itemIndex === -1) return
    userStore.itemList.unshift(userStore.warehouseItemList[itemIndex])
    userStore.warehouseItemList.splice(itemIndex, 1)
  }
}
</script>

<template>
  <div class="warehoust-page page">
    <h2>仓库</h2>
    <div class="item-filter">
      <el-popover class="affix" popper-class="item-filter-wrap" placement="top" width="100%" trigger="click" :teleported="false">
        <template #reference>
          <el-button size="small" type="primary" plain>筛选</el-button>
        </template>
        <p>装备类型</p>
        <el-button
          size="small"
          type="primary"
          plain
          class="item"
          v-for="item of equipTypeList.slice(0, 5)"
          :key="item"
          @click="handleAddFilterType(item)"
          :style="{ borderColor: filterType.includes(item) ? 'red' : '' }"
        >
          {{ equipIcon[item] }}
        </el-button>
        <el-button
          size="small"
          type="primary"
          plain
          class="item"
          v-for="item of equipTypeList.slice(5, 10)"
          :key="item"
          @click="handleAddFilterType(item)"
          :style="{ borderColor: filterType.includes(item) ? 'red' : '' }"
        >
          {{ equipIcon[item] }}
        </el-button>
        <p>装备品质</p>
        <div>
          <el-button
            size="small"
            type="primary"
            plain
            class="item"
            v-for="item of equipQualityList.slice(0, 5)"
            :key="item"
            @click="handleAddFilterQuality(item)"
            :style="{ borderColor: filterQuality.includes(item) ? 'red' : '' }"
          >
            <span :class="item">{{ equipQualityText[item] }}</span>
          </el-button>
        </div>
        <div>
          <el-button
            size="small"
            type="primary"
            plain
            class="item"
            v-for="item of equipQualityList.slice(5)"
            :key="item"
            @click="handleAddFilterQuality(item)"
            :style="{ borderColor: filterQuality.includes(item) ? 'red' : '' }"
            style="margin-top: 10px"
          >
            <span :class="item">{{ equipQualityText[item] }}</span>
          </el-button>
        </div>
        <p>搜索名称</p>
        <el-input size="small" v-model="filterName"></el-input>
        <p>搜索词条</p>
        <el-input size="small" v-model="filterAffix"></el-input>
        <br />
        <br />
        <el-button size="small" type="success" plain @click="handleResetFilter">重置</el-button>
      </el-popover>
    </div>

    <div class="main">
      <div class="item-list">
        <div class="border-wrap">
          <h4>背包</h4>
          <div style="display: flex; align-items: center; padding: 0 5px; margin-bottom: 3px">
            <span style="font-size: 12px; margin-right: 4px">{{ userStore.getItemEquipTotal }} / {{ userStore.getItemCapacity }}</span>
            <el-button size="small" type="success" plain @click="handleArrange">整理</el-button>
          </div>
          <div style="display: flex; align-items: center; padding: 0 5px">
            <span style="font-size: 12px">查看装备信息：</span>
            <el-switch v-model="isShowEquipInfo" size="small" @change="equipInfo = undefined" />
          </div>
          <div class="item-btn-wrap btn-wrap">
            <el-row :gutter="10">
              <el-col :span="24" v-for="item of itemList" :key="item.id">
                <CommonBtn
                  size="small"
                  @done="warehouseAction(item, 'warehouseDeposit')"
                  :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
                >
                  <span v-html="getEquipName(item, item.type)"></span>
                </CommonBtn>
              </el-col>
            </el-row>
          </div>
        </div>
        <div class="border-wrap" style="padding: 10px; min-height: 50%; margin-top: 5px" v-show="isShowEquipInfo">
          <h4>装备信息</h4>
          <template v-if="equipInfo">
            <EquipInfo :equip-info="equipInfo" :show-action="false" :show-lock="false" />
          </template>
        </div>
      </div>
      <div class="item-list">
        <div class="border-wrap">
          <h4>仓库</h4>
          <div style="display: flex; align-items: center; padding: 0 5px">
            <span style="font-size: 12px; margin-right: 4px">
              {{ userStore.getWarehouseItemEquipInfoList.length }} / {{ userStore.getWarehouseCapacity }}
            </span>
            <el-button size="small" type="success" plain @click="handleArrangeWarehouse">整理</el-button>
          </div>
          <div class="item-btn-wrap btn-wrap">
            <el-row :gutter="10">
              <el-col :span="24" v-for="item of warehouseItemList" :key="item.id">
                <CommonBtn
                  size="small"
                  @done="warehouseAction(item, 'warehouseTakeOut')"
                  :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
                >
                  <span v-html="getEquipName(item, item.type)"> </span>
                </CommonBtn>
              </el-col>
            </el-row>
          </div>
        </div>
      </div>
    </div>
    <FooterBar />
  </div>
</template>

<style lang="scss" scoped>
.warehoust-page {
  padding-top: 0;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  .item-filter {
    margin-bottom: 10px;
    > button {
      width: 100%;
    }
  }
  .main {
    display: flex;
    justify-content: space-between;
    overflow: hidden;
    flex: 1;
  }
  .item-list {
    width: calc(50% - 5px);
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    .item-btn-wrap {
      overflow: auto;
    }
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
    .border-wrap {
      overflow: hidden;
      display: flex;
      flex-flow: column nowrap;
      height: 100%;
    }
  }
  .btn-wrap {
    & > div {
      justify-content: unset;
      margin-bottom: 0;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
}
</style>
